// US-GIFT-01 -- Guardian records a gift deposit for a child.

import { giftCreate } from '@chorepop/core/client';
import { type Component, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { AmountPad } from '../components/AmountPad';

type Props = {
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  displayCurrencyCode: string;
  onDone: (depositId: number, autoAllocated: boolean) => void;
  onCancel: () => void;
};

export const RecordGiftScreen: Component<Props> = (props) => {
  const [amount, setAmount] = createSignal('');
  const [note, setNote] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const amountValid = () => {
    const value = Number(amount().replace(',', '.'));
    return !Number.isNaN(value) && value > 0;
  };
  const noteValid = () => note().length <= 120;

  const submit = async () => {
    if (!amountValid() || !noteValid()) return;
    setBusy(true);
    setError(null);
    const result = await giftCreate({
      sessionId: props.sessionId,
      childAccountId: props.childAccountId,
      amountMinorUnits: Math.round(Number(amount().replace(',', '.')) * 100),
      note: note().trim() || null,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message ?? 'Something went wrong.');
      return;
    }
    props.onDone(result.value.depositId, result.value.autoAllocated);
  };

  return (
    <ActionScreen
      eyebrow="A gift is theirs to split"
      title="Record a gift"
      onBack={props.onCancel}
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          onClick={submit}
          disabled={busy() || !amountValid() || !noteValid()}
        >
          {busy() ? 'Sending…' : `Send to ${props.childDisplayName} to allocate`}
        </button>
      }
    >
      <section class="action-callout action-callout--warm">
        <span aria-hidden="true">↗</span>
        <p>
          Gifts do not use the usual split. {props.childDisplayName} chooses which buckets receive
          this money.
        </p>
      </section>
      <AmountPad
        id="gift-amount"
        label="Amount"
        value={amount()}
        currencyCode={props.displayCurrencyCode}
        disabled={busy()}
        onChange={setAmount}
      />
      <label class="action-note-field" for="gift-note">
        <span>
          Gift note <em>Optional · shown to {props.childDisplayName}</em>
        </span>
        <input
          id="gift-note"
          type="text"
          maxLength={120}
          placeholder="For example, birthday gift from Grandma"
          value={note()}
          onInput={(event) => setNote(event.currentTarget.value)}
          disabled={busy()}
        />
        <small>{note().length}/120</small>
      </label>
      <Show when={!noteValid()}>
        <span class="field-error">A gift note can be up to 120 characters.</span>
      </Show>
      <Show when={error()}>
        {(message) => (
          <div class="form-error" role="alert">
            {message()}
          </div>
        )}
      </Show>
    </ActionScreen>
  );
};
